import { useState } from 'react'
import { User } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { Skeleton } from '@/components/Skeleton'
import { trackImageError } from '@/utils/imageTelemetry'

interface AvatarProps {
    src?: string | null
    alt: string
    size?: 'xs' | '2xs' | 'sm' | 'md' | 'lg' | 'xl'
    className?: string
    isLoading?: boolean
    fallback?: string
    fallbackIcon?: LucideIcon
}

export function Avatar({
    src,
    alt,
    size = 'md',
    className = '',
    isLoading = false,
    fallback,
    fallbackIcon: FallbackIcon = User,
}: AvatarProps) {
    const sizeClasses = {
        xs: 'w-5 h-5',
        '2xs': 'w-7 h-7',
        sm: 'w-8 h-8',
        md: 'w-10 h-10',
        lg: 'w-24 h-24',
        xl: 'w-32 h-32',
    }

    const iconSizes = {
        xs: 'w-3 h-3',
        '2xs': 'w-3.5 h-3.5',
        sm: 'w-4 h-4',
        md: 'w-5 h-5',
        lg: 'w-12 h-12',
        xl: 'w-16 h-16',
    }

    const [hasError, setHasError] = useState(false)

    // An empty src string is an invalid image URL, same as missing — fall back either way.
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    const imgSrc = src || fallback
    const containerClasses = `rounded-full overflow-hidden bg-surface ${sizeClasses[size]} ${className}`

    if (isLoading) {
        return (
            <div className={containerClasses}>
                <Skeleton className="w-full h-full" />
            </div>
        )
    }

    if (!imgSrc || hasError) {
        return (
            <div
                className={`${containerClasses} flex items-center justify-center bg-gray-200 dark:bg-gray-700`}
            >
                <FallbackIcon
                    className={`${iconSizes[size]} text-gray-400 dark:text-gray-500`}
                    aria-hidden="true"
                />
            </div>
        )
    }

    return (
        <div className={containerClasses}>
            <img
                src={imgSrc}
                alt={alt}
                onError={() => {
                    trackImageError('avatar', imgSrc)
                    setHasError(true)
                }}
                className="w-full h-full object-cover bg-gray-800"
            />
        </div>
    )
}
